import { isVmError, vmError } from './errors.js';
/**
 * `manifest.json`: what the image build produced, and whether this code can
 * boot it.
 *
 * The manifest is the only mutable file in the asset bucket and the first
 * thing the app fetches, so everything downstream of it — a 13 MB state, a
 * 192 MB disk read by range — depends on it being right. It is validated
 * field by field here rather than trusted, because a half-written or
 * half-uploaded manifest otherwise surfaces as a guest that boots and then
 * misbehaves.
 *
 * The constants below are the other half of the boot fingerprint. They live
 * in code, not in the manifest, precisely so that a mismatch between the
 * code and the image a state was saved from is a refusal rather than silent
 * corruption (docs/browser.md, Risks).
 */
/**
 * Exact v86 the app is pinned to. A saved state is only restorable by the
 * build that produced it, and this string is what says so.
 *
 * It is the installed package's own `version` field, npm build metadata and
 * all — `0.5.458+gd96be77`, not the `0.5.458` this package's package.json
 * asks for. That is what `scripts/build-image.sh` reads out of
 * node_modules/v86/package.json and writes into the manifest, so anything
 * shorter here makes every state unrestorable with a fingerprint error.
 * Bumping the dependency means bumping this, and CI rebuilds the state.
 */
export const V86_VERSION = '0.5.458+gd96be77';
/**
 * The kernel command line, byte for byte from the spike (spike/web/vm-options.js).
 *
 * `init_on_free=on` zeroes pages as the kernel frees them, so a saved state
 * compresses to the memory actually in use rather than to everything ever
 * touched (the v86 maintainers' recipe for small states, v86 issue #813).
 * It was worth ~15 % of the state in phase 0.
 */
export const CMDLINE = 'console=ttyS0,115200 root=/dev/sda rw rootfstype=ext4 tsc=reliable mitigations=off init_on_free=on quiet';
/** Guest RAM, in MiB. Phase 0's number, and what the state was saved under. */
export const MEMORY_MB = 256;
/** VGA memory, in MiB. Nothing draws on it; v86 wants a size anyway. */
export const VGA_MEMORY_MB = 2;
/** Extra UARTs: ttyS1 (exec) and ttyS2 (command done). ttyS0 always exists. */
export const UARTS = 2;
/** The five assets the emulator boots from. `state.bin.zst` is apart: it is optional. */
export const BOOT_ASSETS = ['rootfs.img', 'vmlinuz', 'initramfs', 'seabios.bin', 'vgabios.bin'];
/** How long one attempt at the manifest (about 10 KB) may take before it is presumed stalled. */
const MANIFEST_ATTEMPT_MS = 20_000;
const MANIFEST_ATTEMPTS = 3;
/**
 * Fetch and validate `manifest.json`. Throws a `VmError` with code `manifest`.
 *
 * Each attempt has a time limit and a network failure is tried again: a
 * connection that goes quiet mid-response leaves `fetch` pending for ever,
 * which on 2026-10-01 was measured to hang the boot screen with no error and
 * no way on. An answer from the server (a 4xx, a body that is not a manifest)
 * is not retried.
 */
export async function fetchManifest(url, opts = {}) {
    let body;
    for (let n = 1;; n++) {
        const controller = new AbortController();
        const onAbort = () => controller.abort();
        opts.signal?.addEventListener('abort', onAbort, { once: true });
        if (opts.signal?.aborted)
            controller.abort();
        const timer = setTimeout(() => controller.abort(), MANIFEST_ATTEMPT_MS);
        let failure = null;
        try {
            const res = await fetch(url, { signal: controller.signal, cache: 'no-cache' });
            if (!res.ok) {
                const err = vmError('manifest', `the VM manifest at ${url} answered ${res.status}`);
                if (res.status < 500 && res.status !== 408 && res.status !== 429)
                    throw Object.assign(err, { final: true });
                throw err;
            }
            const text = await res.text();
            try {
                body = JSON.parse(text);
            }
            catch (err) {
                throw Object.assign(vmError('manifest', `the VM manifest at ${url} is not JSON`, err), { final: true });
            }
        }
        catch (err) {
            if (err.final || opts.signal?.aborted) {
                throw isVmError(err) ? err : vmError('manifest', `could not fetch the VM manifest at ${url}`, err);
            }
            failure = isVmError(err)
                ? err
                : vmError('manifest', controller.signal.aborted
                    ? `the VM manifest at ${url} did not arrive within ${MANIFEST_ATTEMPT_MS / 1000} s`
                    : `could not fetch the VM manifest at ${url}`, err);
        }
        finally {
            clearTimeout(timer);
            opts.signal?.removeEventListener('abort', onAbort);
        }
        if (failure === null)
            break;
        if (n >= MANIFEST_ATTEMPTS)
            throw failure;
        await new Promise((resolve) => setTimeout(resolve, 1_000 * n));
    }
    return validateManifest(body);
}
function bad(what) {
    throw vmError('manifest', `the VM manifest is unusable: ${what}`);
}
function asRecord(value, what) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        bad(`${what} is not an object`);
    return value;
}
function str(source, key, what) {
    const value = source[key];
    if (typeof value !== 'string' || value.length === 0)
        bad(`${what}.${key} is missing or not a string`);
    return value;
}
function num(source, key, what) {
    const value = source[key];
    if (typeof value !== 'number' || !Number.isFinite(value))
        bad(`${what}.${key} is missing or not a number`);
    return value;
}
function asset(value, name) {
    const raw = asRecord(value, `files["${name}"]`);
    const path = str(raw, 'path', `files["${name}"]`);
    const bytes = num(raw, 'bytes', `files["${name}"]`);
    const sha256 = str(raw, 'sha256', `files["${name}"]`);
    if (bytes <= 0)
        bad(`files["${name}"].bytes is ${bytes}`);
    if (!/^[0-9a-f]{64}$/.test(sha256))
        bad(`files["${name}"].sha256 is not a lowercase hex SHA-256`);
    return { path, bytes, sha256 };
}
/**
 * Turn parsed JSON into a `VmManifest`, or explain why it cannot be one.
 *
 * Exported so the app can validate a manifest it obtained by other means (a
 * cache, a bundled copy) under exactly these rules.
 */
export function validateManifest(value) {
    const raw = asRecord(value, 'the manifest');
    if (raw['schema'] !== 1)
        bad(`schema is ${JSON.stringify(raw['schema'])}, and this build only reads schema 1`);
    const rawFiles = asRecord(raw['files'], 'files');
    const files = Object.fromEntries(BOOT_ASSETS.map((name) => [name, asset(rawFiles[name], name)]));
    if (rawFiles['state.bin.zst'] !== undefined)
        files['state.bin.zst'] = asset(rawFiles['state.bin.zst'], 'state.bin.zst');
    const rawBoot = asRecord(raw['boot'], 'boot');
    const boot = {
        v86: str(rawBoot, 'v86', 'boot'),
        cmdline: str(rawBoot, 'cmdline', 'boot'),
        memoryMb: num(rawBoot, 'memoryMb', 'boot'),
        vgaMemoryMb: num(rawBoot, 'vgaMemoryMb', 'boot'),
        uarts: num(rawBoot, 'uarts', 'boot'),
        rootfs: str(rawBoot, 'rootfs', 'boot'),
        kernel: str(rawBoot, 'kernel', 'boot'),
        initrd: str(rawBoot, 'initrd', 'boot'),
        bios: str(rawBoot, 'bios', 'boot'),
        vgaBios: str(rawBoot, 'vgaBios', 'boot'),
        hash: str(rawBoot, 'hash', 'boot'),
    };
    return {
        schema: 1,
        builtAt: str(raw, 'builtAt', 'the manifest'),
        alpine: str(raw, 'alpine', 'the manifest'),
        kernel: str(raw, 'kernel', 'the manifest'),
        v86: str(raw, 'v86', 'the manifest'),
        files,
        boot,
        levels: levels(raw['levels']),
    };
}
/**
 * `levels`: mission id -> seed values. An older manifest listed the ids as
 * an array and left the values in the guest as /levels/<id>.seed, readable
 * from the learner's terminal; that shape is refused outright rather than
 * accepted with empty values, because a level seeded without its values
 * grades every templated check against the empty string and fails an honest
 * learner who did everything right.
 */
function levels(value) {
    if (Array.isArray(value))
        bad('levels is an array of ids; this build needs the seed values, which an older image kept in the guest');
    const raw = asRecord(value, 'levels');
    const out = {};
    for (const [id, values] of Object.entries(raw)) {
        const seed = asRecord(values, `levels["${id}"]`);
        for (const [key, v] of Object.entries(seed)) {
            if (!/^[A-Za-z0-9_]+$/.test(key))
                bad(`levels["${id}"] has the seed key "${key}", which is not [A-Za-z0-9_]+`);
            if (typeof v !== 'string')
                bad(`levels["${id}"].${key} is not a string`);
        }
        out[id] = seed;
    }
    return out;
}
/**
 * Absolute URL of one asset. `base` is `VmBootOptions.assetBase`, with or
 * without its trailing slash, and must be absolute: v86 is handed these
 * strings and resolves nothing itself. A page-relative base such as `/vm/`
 * otherwise fails here as a bare "Invalid base URL" from the URL
 * constructor, half a second into a boot, with nothing naming the option.
 */
export function assetUrl(base, file) {
    try {
        return new URL(file.path, base.endsWith('/') ? base : `${base}/`).href;
    }
    catch (err) {
        throw vmError('manifest', `assetBase must be an absolute URL, and is "${base}"`, err);
    }
}
async function sha256Hex(text) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
/**
 * The fingerprint of the machine *this* code is about to build.
 *
 * Five fields come from the constants above and five from the manifest's
 * current asset hashes, which is what makes the comparison worth making: an
 * asset re-uploaded without rebuilding the state, or a v86 version or
 * cmdline changed in the app without rebuilding the image, each move this
 * hash away from the `boot.hash` the state was saved under.
 *
 * `memoryMb` is the caller's, which for a restore is the manifest's own
 * value (types.ts), so that one field can only differ when a caller has
 * deliberately overridden it — which is only allowed with `restore: false`.
 *
 * The image pipeline computes the same hash when it saves a state. Call
 * this rather than reimplementing it, or the two definitions of "canonical
 * JSON" drift apart on the first field anyone adds.
 */
export async function computeBootFingerprint(files, memoryMb = MEMORY_MB) {
    // Declaration order is part of the definition: JSON.stringify emits keys
    // in insertion order and nothing else, which is the "canonical JSON, no
    // whitespace" types.ts specifies.
    const fields = {
        v86: V86_VERSION,
        cmdline: CMDLINE,
        memoryMb,
        vgaMemoryMb: VGA_MEMORY_MB,
        uarts: UARTS,
        rootfs: files['rootfs.img'].sha256,
        kernel: files.vmlinuz.sha256,
        initrd: files.initramfs.sha256,
        bios: files['seabios.bin'].sha256,
        vgaBios: files['vgabios.bin'].sha256,
    };
    return { ...fields, hash: await sha256Hex(JSON.stringify(fields)) };
}
/**
 * Null when the saved state can be restored here, or a sentence naming the
 * fields that moved. The fields are kept beside the hash for exactly this:
 * "the state does not match" is not a bug report, "v86 0.5.458 -> 0.5.460"
 * is.
 */
export function fingerprintMismatch(runtime, saved) {
    if (runtime.hash === saved.hash)
        return null;
    const moved = Object.keys(runtime)
        .filter((key) => key !== 'hash' && runtime[key] !== saved[key])
        .map((key) => `${key}: the state has ${JSON.stringify(saved[key])}, this build has ${JSON.stringify(runtime[key])}`);
    if (moved.length === 0) {
        return `the fingerprints differ (${saved.hash.slice(0, 12)} against ${runtime.hash.slice(0, 12)}) although every recorded field matches, so the hash itself was computed differently`;
    }
    return moved.join('; ');
}
//# sourceMappingURL=manifest.js.map